// src/pages/ItineraryView.jsx
import React, { useState, useEffect } from "react";
import { itineraryData } from "../data/itinerary";
import { Link } from "react-router-dom";
import {
  MapPin,
  X,
  Music,
  Mic2,
  Coffee,
  Star,
  Loader2,
  Utensils,
  CloudSun,
  CloudRain,
  Sun,
  Snowflake,
  Cloud,
  TicketPercent,
  ZoomIn,
  Check,
  Armchair,
  Save,
  FileSpreadsheet,
  Map,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import RoomNumberCard from "../components/RoomNumberCard";

import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  doc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";

// ... (DateNavigator 保持不變，省略以節省篇幅，請直接保留上一版的代碼) ...
function DateNavigator() {
  // ... (請保留上一版的完整 DateNavigator 代碼) ...
  const [forecast, setForecast] = useState({});
  const [currentWeather, setCurrentWeather] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const currentRes = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=34.6937&longitude=135.5023&current_weather=true&timezone=Asia%2FTokyo",
        );
        const currentData = await currentRes.json();
        setCurrentWeather(currentData.current_weather);
        const dailyRes = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=34.6937&longitude=135.5023&daily=weathercode,temperature_2m_max&timezone=Asia%2FTokyo",
        );
        const data = await dailyRes.json();
        const weatherMap = {};
        if (data.daily && data.daily.time) {
          const offset = 1;
          itineraryData.forEach((day, i) => {
            const apiIndex = i + offset;
            if (data.daily.weathercode[apiIndex] !== undefined) {
              weatherMap[day.day] = {
                maxTemp: Math.round(data.daily.temperature_2m_max[apiIndex]),
                code: data.daily.weathercode[apiIndex],
              };
            }
          });
        }
        setForecast(weatherMap);
      } catch (error) {
        console.error(error);
      }
    };
    fetchWeather();
  }, []);

  const getWeatherIcon = (code) => {
    if (code === undefined)
      return <Loader2 size={12} className="animate-spin text-gray-300" />;
    if (code <= 1) return <Sun size={12} className="text-orange-500" />;
    if (code <= 3) return <CloudSun size={12} className="text-gray-500" />;
    if (code <= 67) return <CloudRain size={12} className="text-blue-500" />;
    if (code <= 77) return <Snowflake size={12} className="text-cyan-400" />;
    if (code > 80) return <CloudRain size={12} className="text-blue-600" />;
    return <Cloud size={12} className="text-gray-400" />;
  };

  const scrollToDay = (day) => {
    const element = document.getElementById(`day-${day}`);
    if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const todayDate = 21;
  return (
    <div className="sticky top-0 z-40 bg-warm-paper/95 backdrop-blur-md py-3 -mx-4 px-4 shadow-sm border-b border-hojicha-light/20 overflow-x-auto no-scrollbar mb-4">
      <div className="flex gap-2 min-w-max">
        {itineraryData.map((day) => {
          const isToday = parseInt(day.date.split("/")[1]) === todayDate;
          const displayTemp =
            isToday && currentWeather
              ? Math.round(currentWeather.temperature)
              : forecast[day.day]?.maxTemp;
          const displayCode =
            isToday && currentWeather
              ? currentWeather.weathercode
              : forecast[day.day]?.code;
          return (
            <button
              key={day.day}
              onClick={() => scrollToDay(day.day)}
              className={`flex flex-col items-center justify-center p-2 rounded-xl min-w-[75px] transition-all active:scale-95 border ${day.highlight ? "bg-white border-gold/50 shadow-sm" : "bg-white/50 border-transparent hover:bg-white"} ${isToday ? "ring-2 ring-starbucks ring-offset-1" : ""}`}
            >
              <span
                className={`text-[10px] font-bold uppercase ${day.highlight ? "text-gold" : "text-gray-400"}`}
              >
                {isToday ? "TODAY" : `Day ${day.day}`}
              </span>
              <span className="text-xs font-bold text-starbucks-dark my-0.5">
                {day.date.split(" ")[0]}
              </span>
              <div className="flex items-center gap-1 bg-gray-100/50 px-1.5 py-0.5 rounded-full mt-1">
                {getWeatherIcon(displayCode)}
                <span className="text-[10px] font-mono text-gray-600">
                  {displayTemp !== undefined ? `${displayTemp}°` : "--"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ... (LyricsModal 保持不變，請保留) ...
function LyricsModal({ isOpen, onClose }) {
  const [selectedSong, setSelectedSong] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen && songs.length === 0) {
      const fetchSongs = async () => {
        setLoading(true);
        try {
          const q = query(collection(db, "songs"));
          const querySnapshot = await getDocs(q);
          const loadedSongs = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setSongs(loadedSongs);
        } catch (error) {
          console.error("Error", error);
        }
        setLoading(false);
      };
      fetchSongs();
    }
  }, [isOpen, songs.length]);

  const filteredSongs = songs.filter((song) =>
    song.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed inset-0 z-[60] bg-warm-paper/95 backdrop-blur-lg flex flex-col pt-12 pb-8 px-6 text-text-primary"
      >
        <div className="flex justify-between items-center mb-8 border-b border-gold/30 pb-4 shrink-0">
          <h2 className="text-3xl font-black text-starbucks-dark tracking-widest flex items-center gap-3 font-serif">
            <Music className="text-gold" /> SETLIST
          </h2>
          <button
            onClick={onClose}
            className="p-2 bg-hojicha-light/30 rounded-full text-hojicha-dark hover:bg-starbucks hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        {!selectedSong && (
          <div className="mb-4 relative">
            <input
              type="text"
              placeholder="搜尋歌名..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border-2 border-hojicha-light/30 rounded-xl py-3 px-4 pl-10 focus:outline-none focus:border-starbucks transition-colors"
            />
            <div className="absolute left-3 top-3.5 text-hojicha">
              <Music size={18} />
            </div>
          </div>
        )}
        {!selectedSong ? (
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="animate-spin text-starbucks" size={32} />
              </div>
            )}
            {filteredSongs.map((song) => (
              <button
                key={song.id}
                onClick={() => setSelectedSong(song)}
                className="w-full text-left p-5 rounded-2xl bg-white border-2 border-hojicha-light/30 hover:border-starbucks hover:bg-starbucks/5 transition-all flex justify-between items-center group shadow-warm"
              >
                <div>
                  {song.act && (
                    <p className="text-[10px] text-hojicha uppercase tracking-wider mb-1">
                      {song.act}
                    </p>
                  )}
                  <span className="font-bold text-xl text-starbucks-dark group-hover:text-starbucks font-serif">
                    {song.title}
                  </span>
                </div>
                <Mic2
                  size={20}
                  className="text-hojicha group-hover:text-starbucks"
                />
              </button>
            ))}
          </div>
        ) : (
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            <button
              onClick={() => setSelectedSong(null)}
              className="mb-6 text-sm text-hojicha hover:text-starbucks flex items-center gap-2 font-bold uppercase tracking-wider shrink-0"
            >
              ← Back to Setlist
            </button>
            <div className="flex-1 overflow-y-auto bg-white rounded-3xl p-8 border-2 border-gold/30 text-center shadow-inner">
              <h3 className="text-3xl font-black text-starbucks mb-2 font-serif">
                {selectedSong.title}
              </h3>
              {selectedSong.note && (
                <p className="text-xs text-gold font-bold mb-6">
                  {selectedSong.note}
                </p>
              )}
              <p className="whitespace-pre-line text-lg leading-loose text-text-primary font-medium font-sans pb-10">
                {selectedSong.lyrics}
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

// --- 子元件 3: 美食選項 Modal (✅ 更新：權限過濾 + Excel 連結 + 商品備註) ---
function OptionModal({ isOpen, onClose, title, options, currentUser }) {
  // ✅ 接收 currentUser
  const [zoomedImage, setZoomedImage] = useState(null);
  const [usedCoupons, setUsedCoupons] = useState(() => {
    const saved = localStorage.getItem("used_coupons");
    return saved ? JSON.parse(saved) : [];
  });

  const toggleUsed = (id) => {
    if (!id) return;
    const isCurrentlyUsed = usedCoupons.includes(id);
    if (!isCurrentlyUsed) {
      const confirmUse = window.confirm("確定要將這張券標記為「已使用」嗎？");
      if (!confirmUse) return;
    }
    const newUsed = isCurrentlyUsed
      ? usedCoupons.filter((uid) => uid !== id)
      : [...usedCoupons, id];
    setUsedCoupons(newUsed);
    localStorage.setItem("used_coupons", JSON.stringify(newUsed));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex justify-center items-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-starbucks p-4 text-white flex justify-between items-center shrink-0">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Utensils size={18} /> {title}
            </h3>
            <button onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="p-4 space-y-4 overflow-y-auto bg-warm-paper">
            {options &&
              options.map((opt, idx) => {
                // ✅ 權限過濾：如果設有 visibleTo，且我不名單中，就不渲染
                if (opt.visibleTo && !opt.visibleTo.includes(currentUser))
                  return null;

                const isUsed = opt.id && usedCoupons.includes(opt.id);
                return (
                  <div
                    key={idx}
                    className={`bg-white p-4 rounded-xl shadow-sm border transition-all ${isUsed ? "border-gray-200 opacity-60 grayscale bg-gray-50" : "border-hojicha/20"}`}
                  >
                    {opt.image && (
                      <div
                        className="relative group cursor-zoom-in"
                        onClick={() => setZoomedImage(opt.image)}
                      >
                        <img
                          src={opt.image}
                          alt={opt.name}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <ZoomIn size={14} />
                        </div>
                        {isUsed && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-red-600/80 text-white font-black border-4 border-white px-4 py-2 rounded-xl rotate-[-15deg] text-xl shadow-lg">
                              USED
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-1">
                      <h4
                        className={`font-bold text-lg ${isUsed ? "text-gray-500 line-through" : "text-starbucks-dark"}`}
                      >
                        {opt.name}
                      </h4>
                      {opt.tag && (
                        <span
                          className={`text-[10px] px-2 py-1 rounded-full font-bold ${isUsed ? "bg-gray-200 text-gray-400" : "bg-gold/20 text-hojicha-dark"}`}
                        >
                          {opt.tag}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary mb-3 whitespace-pre-line">
                      {opt.desc}
                    </p>

                    {/* 商品清單 (唐吉訶德) */}
                    {opt.products && (
                      <div className="flex gap-3 overflow-x-auto pb-2 mb-3 no-scrollbar">
                        {opt.products.map((prod, pIdx) => (
                          <div
                            key={pIdx}
                            className="flex-shrink-0 w-20 flex flex-col items-center gap-1 cursor-pointer group"
                            onClick={() => setZoomedImage(prod.image)}
                          >
                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-100 bg-white relative">
                              <img
                                src={prod.image}
                                alt={prod.name}
                                className="w-full h-full object-contain p-1"
                              />
                              <div className="absolute inset-0 bg-black/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <ZoomIn
                                  size={16}
                                  className="text-white drop-shadow-md"
                                />
                              </div>
                            </div>
                            <span className="text-[10px] text-center text-hojicha font-bold leading-tight">
                              {prod.name}
                            </span>

                            {prod.note && (
                              <span className="text-[9px] text-red-500 font-bold bg-red-50 px-1 rounded">
                                {prod.note}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      {opt.map && (
                        <a
                          href={opt.map}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 text-center py-2 rounded-lg bg-starbucks/10 text-starbucks font-bold hover:bg-starbucks hover:text-white transition-all text-sm flex justify-center items-center gap-1"
                        >
                          <MapPin size={14} /> 開啟地圖
                        </a>
                      )}
                      {opt.hasCoupon && (
                        <Link
                          to="/coupons"
                          className="flex-1 text-center py-2 rounded-lg bg-red-50 text-red-500 font-bold hover:bg-red-500 hover:text-white transition-all text-sm flex justify-center items-center gap-1 border border-red-100"
                        >
                          <TicketPercent size={14} /> 領取優惠券
                        </Link>
                      )}

                      {/* ✅ Excel 連結按鈕 */}
                      {opt.link && (
                        <a
                          href={opt.link}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 text-center py-2 rounded-lg bg-green-50 text-green-600 font-bold hover:bg-green-600 hover:text-white transition-all text-sm flex justify-center items-center gap-1 border border-green-100"
                        >
                          <FileSpreadsheet size={14} /> 代購清單
                        </a>
                      )}

                      {opt.canMarkUsed && (
                        <button
                          onClick={() => toggleUsed(opt.id)}
                          className={`flex-1 text-center py-2 rounded-lg font-bold transition-all text-sm flex justify-center items-center gap-1 border ${isUsed ? "bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200" : "bg-green-50 text-green-600 border-green-200 hover:bg-green-500 hover:text-white"}`}
                        >
                          {isUsed ? (
                            "取消註記"
                          ) : (
                            <>
                              <Check size={14} /> 標記已用
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </motion.div>
      </motion.div>
      {zoomedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/95 flex justify-center items-center p-4 cursor-zoom-out"
          onClick={() => setZoomedImage(null)}
        >
          <button className="absolute top-6 right-6 p-2 bg-white/10 rounded-full text-white hover:bg-white/20">
            <X size={32} />
          </button>
          <motion.img
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            src={zoomedImage}
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ... (ConcertSeatModal 保持不變，省略) ...
function ConcertSeatModal({ isOpen, onClose }) {
  const [seats, setSeats] = useState(Array(8).fill("")); // 8個座位
  const [loading, setLoading] = useState(false);

  // 從 Firebase 讀取座位資料 (所有使用者共享)
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // 使用 onSnapshot 實現即時同步，別人改了你馬上看到
      const unsub = onSnapshot(doc(db, "trip_data", "concert_seats"), (doc) => {
        if (doc.exists()) {
          setSeats(doc.data().seats || Array(8).fill(""));
        }
        setLoading(false);
      });
      return () => unsub();
    }
  }, [isOpen]);

  // 儲存到 Firebase
  const handleSave = async () => {
    try {
      await setDoc(
        doc(db, "trip_data", "concert_seats"),
        { seats },
        { merge: true },
      );
      alert("座位已儲存！大家都能看到了");
    } catch (e) {
      console.error("Save error", e);
      alert("儲存失敗，請檢查網路");
    }
  };

  const handleChange = (index, value) => {
    const newSeats = [...seats];
    newSeats[index] = value;
    setSeats(newSeats);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex justify-center items-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-starbucks-dark p-4 text-white flex justify-between items-center">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Armchair size={18} className="text-gold" /> 演唱會座位表
            </h3>
            <button onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="p-6 bg-warm-paper">
            {loading ? (
              <div className="text-center py-8 text-hojicha">
                <Loader2 className="animate-spin inline mr-2" /> 讀取中...
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-4 text-center">
                  輸入後按下儲存，所有人手機都會同步更新
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {seats.map((seat, idx) => (
                    <div key={idx} className="relative">
                      <span className="absolute -top-2 left-2 text-[10px] bg-starbucks text-white px-1.5 rounded-full font-bold shadow-sm">
                        票 {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={seat}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        placeholder="輸入座位號 (如 A-12)"
                        className="w-full border-2 border-hojicha-light/30 rounded-xl p-3 pt-4 text-center font-bold text-starbucks-dark focus:outline-none focus:border-starbucks bg-white shadow-sm"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleSave}
                  className="w-full bg-gradient-to-r from-starbucks to-[#005c3f] text-white font-bold py-3 rounded-xl shadow-lg flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 transition-all"
                >
                  <Save size={18} /> 儲存座位資訊
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// --- 主元件 ---
export default function ItineraryView({ currentUser }) {
  // ✅ 接收 currentUser
  const [showLyrics, setShowLyrics] = useState(false);
  const [optionModalData, setOptionModalData] = useState(null);
  const [showSeatModal, setShowSeatModal] = useState(false);

  return (
    <div className="space-y-4 pb-24">
      <LyricsModal isOpen={showLyrics} onClose={() => setShowLyrics(false)} />

      {/* ✅ 傳遞 currentUser 給 Modal */}
      <OptionModal
        isOpen={!!optionModalData}
        onClose={() => setOptionModalData(null)}
        title={optionModalData?.title}
        options={optionModalData?.options}
        currentUser={currentUser}
      />

      <ConcertSeatModal
        isOpen={showSeatModal}
        onClose={() => setShowSeatModal(false)}
      />

      <DateNavigator />

      <div className="mt-4">
        <RoomNumberCard />
      </div>
      {/* ✅ 新增：交通地圖快速按鈕區 */}
      <div className="grid grid-cols-1 gap-3 mb-6">
        <a
          href="/maps/osaka_metro.pdf" // 這裡對應你 public 裡的檔案路徑
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-hojicha-light/30 hover:border-starbucks hover:bg-starbucks/5 transition-all group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <Map size={20} />
            </div>
            <div>
              <h3 className="font-bold text-starbucks-dark text-lg">
                大阪地鐵路線圖
              </h3>
              <p className="text-xs text-gray-500">點擊開啟完整 PDF 地圖</p>
            </div>
          </div>
          <span className="text-gray-300 group-hover:text-starbucks group-hover:translate-x-1 transition-all">
            →
          </span>
        </a>
      </div>

      {itineraryData.map((day, index) => (
        <div key={index} id={`day-${day.day}`} className="scroll-mt-36">
          <div
            className={`relative overflow-hidden rounded-3xl p-6 transition-all ${day.highlight ? "bg-white border-2 border-gold shadow-[0_8px_25px_rgba(192,160,96,0.25)]" : "bg-white/80 border border-hojicha-light/50 shadow-warm backdrop-blur-sm"}`}
          >
            {day.highlight && (
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-32 h-32 bg-gold/10 rounded-full blur-3xl pointer-events-none"></div>
            )}

            <div className="flex justify-between items-start mb-6 relative border-b border-dashed border-hojicha-light/50 pb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase ${day.highlight ? "bg-gold text-white" : "bg-starbucks/10 text-starbucks"}`}
                  >
                    Day {day.day}
                  </span>
                  {day.highlight && (
                    <span className="text-xs font-bold text-gold flex items-center">
                      ✨ SPECIAL DAY
                    </span>
                  )}
                </div>
                <h2 className="text-3xl font-black text-starbucks-dark mt-2 font-serif tracking-tight">
                  {day.date}
                </h2>
                <p className="text-base font-bold text-hojicha-dark mt-1">
                  {day.title}
                </p>
              </div>
              <div className="text-hojicha-light/30">
                {day.highlight ? (
                  <Star size={40} className="text-gold" />
                ) : (
                  <Coffee size={40} />
                )}
              </div>
            </div>

            <div className="space-y-6 relative ml-2">
              {day.events.map((event, idx) => {
                // ✅✅✅ 【關鍵修正】在這裡加入權限檢查！ ✅✅✅
                // 如果這個事件有設定 visibleTo，而且目前使用者不在名單內，就直接不顯示 (return null)
                if (event.visibleTo && !event.visibleTo.includes(currentUser)) {
                  return null;
                }

                return (
                  <div
                    key={idx}
                    onClick={() =>
                      event.options &&
                      setOptionModalData({
                        title: event.title,
                        options: event.options,
                        currentUser,
                      })
                    } // ✅ 記得把 currentUser 傳進去給 Modal 用
                    className={`flex gap-4 items-start group relative z-10 ${event.options ? "cursor-pointer hover:bg-starbucks/5 p-2 -m-2 rounded-xl transition-colors" : ""}`}
                  >
                    {/* ... (原本的事件顯示內容，完全保持不變) ... */}
                    {idx !== day.events.length - 1 && (
                      <div className="absolute left-[19px] top-8 bottom-0 w-[2px] bg-hojicha-light/30 -z-10 group-hover:bg-starbucks/30 transition-colors"></div>
                    )}
                    <div
                      className={`relative z-10 w-10 h-10 rounded-full border-2 flex items-center justify-center shadow-sm bg-white transition-all group-hover:scale-110 ${event.isConcert ? "border-gold text-gold" : "border-hojicha-light text-hojicha group-hover:border-starbucks group-hover:text-starbucks"}`}
                    >
                      {event.icon}
                    </div>

                    <div className="flex-1 pb-2">
                      <div className="flex items-baseline justify-between mb-1">
                        <h3
                          className={`font-bold text-lg font-serif leading-tight ${event.isConcert ? "text-starbucks-dark" : "text-text-primary"}`}
                        >
                          {event.title}
                        </h3>
                        <span className="text-sm font-mono font-bold text-hojicha ml-4">
                          {event.time}
                        </span>
                      </div>

                      {event.options && (
                        <span className="text-[10px] text-starbucks font-bold bg-starbucks/10 px-2 py-0.5 rounded-full mt-1 mb-2 inline-block">
                          👆 點擊查看選項
                        </span>
                      )}

                      {event.note && (
                        <p className="text-sm text-text-secondary font-medium leading-relaxed">
                          {event.note}
                        </p>
                      )}

                      {event.map && (
                        <a
                          href={event.map}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-starbucks-dark mt-3 hover:text-starbucks font-bold transition-colors bg-starbucks/10 px-3 py-1.5 rounded-full hover:bg-starbucks/20"
                        >
                          <MapPin size={14} /> 開啟地圖
                        </a>
                      )}

                      {/* ... (Concert 按鈕區塊保持不變) ... */}
                      {event.isConcert && (
                        <div className="mt-4 flex flex-col gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSeatModal(true);
                            }}
                            className="w-full bg-white text-starbucks-dark text-sm font-bold py-3 px-4 rounded-2xl shadow-sm border-2 border-gold/30 flex items-center justify-center gap-2 hover:bg-gold/10 transition-all active:scale-95 font-serif"
                          >
                            <Armchair size={18} className="text-gold" />{" "}
                            <span className="tracking-wider">
                              查看/輸入座位號碼
                            </span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLyrics(true);
                            }}
                            className="w-full bg-gradient-to-r from-starbucks to-[#005c3f] text-white text-sm font-bold py-3 px-4 rounded-2xl shadow-md flex items-center justify-center gap-2 hover:scale-[1.02] transition-all hover:shadow-lg active:scale-95 border border-gold/30 font-serif"
                          >
                            <Music size={18} className="text-gold" />{" "}
                            <span className="tracking-wider">
                              開啟演唱會歌單
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
